import { featureCollection } from '@turf/helpers';
import { bbox } from '@turf/turf';
import { getCenterOfBoundingBox } from 'screens/Property/helpers/mapApiHelpers';
import { ApplicationPropertyVO } from '../../../hooks/useApplicationProperties';

interface CropZoneData {
  cropZoneId: string;
  cropZoneName: string;
  cropName: string;
  appliedArea: number;
  cropZoneArea: number;
}

interface FieldData {
  fieldId: string;
  fieldName: string;
  fieldGeometry: any;
  farmId: string;
  farmName: string;
  farmArchived: boolean;
  fieldArchived: boolean;
  cropZones: CropZoneData[];
}

interface GeoJSONFeatureProperties {
  id: string;
  fieldId: string;
  fieldName: string;
  name: string;
  defaultLabel: string;
  farmId: string;
  farmName: string;
  archived: boolean;
  cropZoneCount: number;
  totalAppliedArea: number;
  cropZones: CropZoneData[];
  $layer?: string;
}

interface GeoJSONFeature {
  type: 'Feature';
  id: string;
  properties: GeoJSONFeatureProperties;
  geometry: any;
}

interface GeoJSONFeatureCollection {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
}

/**
 * Extract coordinates from GeoJSON geometry
 */
export const extractCoordinatesFromGeometry = (geometry: any): number[][] => {
  if (!geometry) return [];

  if (geometry.type === 'Polygon') {
    return geometry.coordinates[0];
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates[0][0];
  }
  return [];
};

/**
 * Calculate centroid from an array of coordinates
 */
export const calculateCentroid = (
  coords: number[][]
): [number, number] | null => {
  if (!coords || coords.length === 0) return null;

  const avgLng =
    coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
  const avgLat =
    coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;

  return [avgLng, avgLat];
};

/**
 * Calculate centroid from GeoJSON features
 */
export const calculateCentroidFromFeatures = (
  features: GeoJSONFeature[]
): [number, number] | null => {
  const coords = features
    .filter(f => f.geometry?.coordinates)
    .flatMap(f => extractCoordinatesFromGeometry(f.geometry));

  return calculateCentroid(coords);
};

/**
 * Calculate bounding box center from GeoJSON features.
 * Returns the midpoint of the extent, matching what map.fitBounds() uses.
 */
export const calculateBoundingBoxCenter = (
  features: GeoJSONFeature[]
): [number, number] | null => {
  const validFeatures = features.filter(f => f.geometry?.coordinates);
  if (validFeatures.length === 0) return null;

  const bounds = bbox(featureCollection(validFeatures));
  return getCenterOfBoundingBox(bounds) as [number, number];
};

/**
 * Pick a zoom level that fits the given lat/lng bbox into the drawer's map
 * viewport. PropertyMap doesn't expose mapbox-gl's `fitBounds` for us to
 * delegate to, so we compute the zoom ourselves and feed it through its
 * plain `zoom` prop.
 *
 * Math is the standard Web Mercator fitBounds formula: project each
 * latitude to its Mercator y-coordinate, compute the bbox's fraction of
 * the world in both axes, then pick the smaller of the lat/lng zooms so
 * the longer axis fits. This matches mapbox-gl's own fitBounds — a
 * degrees-only approximation under-estimates zoom at higher latitudes
 * because longitude degrees shrink with cos(latitude), so fields outside
 * the equator would render too tight or too loose.
 *
 * (Implemented inline rather than via `@mapbox/geo-viewport` because that
 *  library uses an implicit-global `antiMeridian` variable that throws
 *  ReferenceError under TS strict mode — see geo-viewport's `fetchMerc`.)
 *
 * Defaults assume a ~600px-wide drawer map panel with ~80px padding.
 * Result is clamped to [0, 18].
 */
const TILE_SIZE = 256;
const latToMercator = (latDeg: number) => {
  const latRad = (latDeg * Math.PI) / 180;
  const sin = Math.sin(latRad);
  return Math.log((1 + sin) / (1 - sin)) / 2;
};

export const calculateFitZoom = (
  features: GeoJSONFeature[],
  viewportPx = 600,
  paddingPx = 80
): number => {
  const validFeatures = features.filter(f => f.geometry?.coordinates);
  if (validFeatures.length === 0) return 10;

  const [minLng, minLat, maxLng, maxLat] = bbox(
    featureCollection(validFeatures)
  );
  if (minLng === maxLng && minLat === maxLat) {
    return 16; // Single point — pick a city-scale zoom.
  }

  const usable = Math.max(50, viewportPx - paddingPx * 2);
  const latFraction = (latToMercator(maxLat) - latToMercator(minLat)) / Math.PI;
  const lngFraction = (maxLng - minLng) / 360;

  // Solve `usable = TILE_SIZE * 2^zoom * fraction` for zoom, then pick the
  // smaller of the two axes so the larger one still fits.
  const latZoom =
    latFraction > 0 ? Math.log2(usable / TILE_SIZE / latFraction) : Infinity;
  const lngZoom =
    lngFraction > 0 ? Math.log2(usable / TILE_SIZE / lngFraction) : Infinity;
  const zoom = Math.min(latZoom, lngZoom);

  return Math.max(0, Math.min(18, Math.floor(zoom)));
};

/**
 * Create a GeoJSON feature from field data
 */
const createFieldFeature = (
  field: FieldData,
  selectedKeys: string[]
): GeoJSONFeature => {
  const totalAppliedArea = field.cropZones.reduce(
    (sum, cz) => sum + cz.appliedArea,
    0
  );

  // Determine if field is archived (either farm or field is archived)
  const isArchived = field.farmArchived || field.fieldArchived;

  // Check if this field, farm, or any of its crop zones is selected
  const isSelected =
    selectedKeys.includes(field.fieldId) ||
    selectedKeys.includes(field.farmId) ||
    field.cropZones.some(cz => selectedKeys.includes(cz.cropZoneId));

  return {
    type: 'Feature',
    id: field.fieldId,
    properties: {
      id: field.fieldId,
      fieldId: field.fieldId,
      fieldName: field.fieldName,
      name: field.fieldName, // For map label (newer system)
      defaultLabel: field.fieldName, // For map label (feature flag system)
      farmId: field.farmId,
      farmName: field.farmName,
      archived: isArchived, // For archived indicator
      cropZoneCount: field.cropZones.length,
      totalAppliedArea,
      cropZones: field.cropZones,
      // Custom layer name (not "focused") so PropertyFeatureLayer's
      // bbox-fit effect — which auto-fitBounds when $layer === FOCUSED_LAYER_TYPE
      // changes — doesn't fire on every select/deselect. The blue highlight
      // still applies because customStyles below uses the same key.
      $layer: isSelected ? 'aw-applied-area-selected' : undefined
    },
    geometry: field.fieldGeometry
  };
};

/**
 * Transform properties to GeoJSON format
 * Groups by farms and fields to avoid duplicate geometries
 */
export const transformPropertiesToGeoJSON = (
  properties: ApplicationPropertyVO[],
  selectedKeys: string[]
): GeoJSONFeatureCollection => {
  if (!properties || properties.length === 0) {
    return {
      type: 'FeatureCollection',
      features: []
    };
  }

  // Group by farm and field to get unique field geometries
  const farmFieldMap = new Map<string, Map<string, FieldData>>();

  // eslint-disable-next-line no-restricted-syntax
  for (const prop of properties) {
    if (prop.fieldGeometry) {
      if (!farmFieldMap.has(prop.farmId)) {
        farmFieldMap.set(prop.farmId, new Map());
      }

      const farmFields = farmFieldMap.get(prop.farmId)!;
      if (!farmFields.has(prop.fieldId)) {
        // Store unique field with its geometry and aggregated data
        farmFields.set(prop.fieldId, {
          fieldId: prop.fieldId,
          fieldName: prop.fieldName,
          fieldGeometry: prop.fieldGeometry,
          farmId: prop.farmId,
          farmName: prop.farmName,
          farmArchived: prop.farmArchived,
          fieldArchived: prop.fieldArchived,
          cropZones: []
        });
      }

      // Add crop zone info to the field
      const cropZoneData: CropZoneData = {
        cropZoneId: prop.cropZoneId,
        cropZoneName: prop.cropZoneName,
        cropName: prop.cropName,
        appliedArea: prop.appliedArea,
        cropZoneArea: prop.cropZoneArea ?? 0
      };

      farmFields.get(prop.fieldId)!.cropZones.push(cropZoneData);
    }
  }

  // Convert to GeoJSON features - one feature per unique field
  const features: GeoJSONFeature[] = [];
  const farmFieldsArray = Array.from(farmFieldMap.values());

  // eslint-disable-next-line no-restricted-syntax
  for (const farmFields of farmFieldsArray) {
    const fieldsArray = Array.from(farmFields.values());

    // eslint-disable-next-line no-restricted-syntax
    for (const field of fieldsArray) {
      features.push(createFieldFeature(field, selectedKeys));
    }
  }

  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Find field coordinates by field ID in GeoJSON collection
 */
export const findFieldCoordinates = (
  features: GeoJSONFeature[],
  fieldId: string
): [number, number] | null => {
  const fieldFeature = features.find(f => f.id === fieldId);

  if (!fieldFeature?.geometry) {
    return null;
  }

  const coords = extractCoordinatesFromGeometry(fieldFeature.geometry);
  return calculateCentroid(coords);
};

/**
 * Find farm coordinates by farm ID in GeoJSON collection
 * Calculates centroid across all fields in the farm
 */
export const findFarmCoordinates = (
  features: GeoJSONFeature[],
  farmId: string
): [number, number] | null => {
  const farmFields = features.filter(f => f.properties.farmId === farmId);

  if (farmFields.length === 0) {
    return null;
  }

  const allCoords: number[][] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const fieldFeature of farmFields) {
    if (fieldFeature.geometry) {
      const coords = extractCoordinatesFromGeometry(fieldFeature.geometry);
      allCoords.push(...coords);
    }
  }

  return calculateCentroid(allCoords);
};

/**
 * Get all expandable keys from properties for auto-expand during search
 * Returns farm IDs, field IDs (with context), and crop IDs based on sortBy
 */
export const getAllExpandableKeys = (
  properties: ApplicationPropertyVO[],
  sortBy: string,
  searchTerm?: string
): string[] => {
  const keys: Set<string> = new Set();
  const lowerSearch = searchTerm?.toLowerCase().trim();

  if (sortBy === 'BY_CROP') {
    // Group by Crop -> expand crops, farms within crops, and fields within farms
    // eslint-disable-next-line no-restricted-syntax
    for (const prop of properties) {
      const cropKey = prop.cropName || 'Unknown';
      const cropId = `crop-${cropKey}`;
      const farmKey = `${prop.farmId}-in-${cropId}`;
      const fieldKey = `${prop.fieldId}-in-${farmKey}`;

      keys.add(cropId);
      keys.add(farmKey);
      keys.add(fieldKey);
    }
  } else {
    // Group by Farm -> expand farms and conditionally expand fields
    // eslint-disable-next-line no-restricted-syntax
    for (const prop of properties) {
      keys.add(prop.farmId);

      // Only expand field if: no search OR crop zone/crop name matches
      const shouldExpandField =
        !lowerSearch ||
        prop.cropZoneName.toLowerCase().includes(lowerSearch) ||
        prop.cropName.toLowerCase().includes(lowerSearch);

      if (shouldExpandField) {
        keys.add(`${prop.fieldId}-in-${prop.farmId}`);
      }
    }
  }

  return Array.from(keys);
};
